export type MessageStorage = "todo" | "team_inbox" | "personal" | "context";
export type MessageViewKind = "original" | "link";
export type MessageKind = "email" | "internal" | "other";

export interface UiMessageState {
  storage: MessageStorage;
  viewKind: MessageViewKind;
  originalStorage: MessageStorage | null;
  messageType: MessageKind;
  supportsComment: boolean;
  supportsReply: boolean;
  supportsForward: boolean;
}

interface DeriveUiMessageStateParams {
  listType?: string;
  selectedMessage?: any;
  detailMessage?: any;
  forceContextStorage?: boolean;
  defaultMessageNodeId?: string | number | null;
}

const INTERNAL_MESSAGE_TYPES = new Set(["1", "6", "12"]);

const toMessageKind = (type: unknown): MessageKind => {
  const normalizedType = String(type ?? "");

  if (normalizedType === "7") {
    return "email";
  }

  if (INTERNAL_MESSAGE_TYPES.has(normalizedType)) {
    return "internal";
  }

  return "other";
};

const supportsDiscussionForKind = (kind: MessageKind): boolean => {
  // Spec: discussion/commenting is meaningful for context communication threads
  // (emails + internal notices). Other items are not guaranteed to support it.
  return kind === "email" || kind === "internal";
};

const normalizeNumericId = (
  value: string | number | null | undefined,
): string | null => {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text || text === "0" || text.toLowerCase() === "null") return null;
  return text;
};

const getMessageContainerId = (
  selectedMessage: any,
  detailMessage: any,
): string | null =>
  normalizeNumericId(selectedMessage?.treeHandle ?? detailMessage?.treeHandle);

const toStorage = (
  listType: string | undefined,
  forceContextStorage: boolean,
  defaultMessageNodeId?: string | number | null,
  selectedMessage?: any,
  detailMessage?: any,
): MessageStorage => {
  if (forceContextStorage) {
    return "context";
  }

  const defaultContainerId = normalizeNumericId(defaultMessageNodeId);
  const currentContainerId = getMessageContainerId(selectedMessage, detailMessage);

  if (
    defaultContainerId != null &&
    currentContainerId != null &&
    defaultContainerId === currentContainerId
  ) {
    return "todo";
  }

  switch (listType) {
    case "todo":
      return "todo";
    case "corporate":
      return "team_inbox";
    case "messages":
    case "sent":
    case "trash":
    default:
      return "personal";
  }
};

const isLinkedMessage = (selectedMessage: any, detailMessage: any): boolean => {
  const followReference = String(
    selectedMessage?.followReference ?? detailMessage?.followReference ?? "0",
  );

  return followReference !== "0" && followReference !== "";
};

const isStoredInContext = (selectedMessage: any, detailMessage: any): boolean =>
  Boolean(
    detailMessage?.folderId ||
      detailMessage?.projectId ||
      detailMessage?.activityRefId ||
      detailMessage?.folderRefId ||
      detailMessage?.projectRefId ||
      selectedMessage?.folderId ||
      selectedMessage?.projectId ||
      selectedMessage?.activityRefId ||
      selectedMessage?.folderRefId ||
      selectedMessage?.projectRefId,
  );

export const deriveUiMessageState = ({
  listType,
  selectedMessage,
  detailMessage,
  forceContextStorage = false,
  defaultMessageNodeId,
}: DeriveUiMessageStateParams): UiMessageState => {
  const storage = toStorage(
    listType,
    forceContextStorage,
    defaultMessageNodeId,
    selectedMessage,
    detailMessage,
  );
  const viewKind = isLinkedMessage(selectedMessage, detailMessage)
    ? "link"
    : "original";
  const messageType = toMessageKind(
    selectedMessage?.type ?? detailMessage?.type,
  );
  const supportsComment = supportsDiscussionForKind(messageType);
  const originalStorage =
    viewKind === "link"
      ? isStoredInContext(selectedMessage, detailMessage)
        ? "context"
        : storage
      : null;

  return {
    storage,
    viewKind,
    originalStorage,
    messageType,
    supportsComment,
    supportsReply: messageType === "email",
    supportsForward: messageType === "email",
  };
};

export const isResolveVisible = (state: UiMessageState): boolean =>
  state.viewKind === "original" && state.storage !== "context";

export const isMarkDoneVisible = (state: UiMessageState): boolean =>
  state.viewKind === "link";

export const isCommentVisible = (state: UiMessageState): boolean => {
  if (!state.supportsComment) {
    return false;
  }

  if (state.viewKind === "link") {
    // Client: comment is visible; discussion targets original, never the link.
    return true;
  }

  // Client: comment is visible always. If outside context, UI must force context selection
  // and move the original message before creating the notice.
  return true;
};

export const isDeleteVisible = (state: UiMessageState): boolean =>
  state.viewKind === "original";

export const requiresDeleteConfirmation = (state: UiMessageState): boolean =>
  state.viewKind === "original" && state.storage === "context";

export const getDiscussionTargetMessageId = (
  selectedMessage?: any,
  detailMessage?: any,
): string | null => {
  const followReference = String(
    selectedMessage?.followReference ?? detailMessage?.followReference ?? "0",
  );

  // Some list rows use followReference as a boolean-like link flag ("1") while the real
  // referenced/original message id is provided as `includeMessage`. Prefer includeMessage
  // when followReference is not a plausible message id.
  const includeMessage = String(
    selectedMessage?.includeMessage ?? detailMessage?.includeMessage ?? "",
  ).trim();

  const followNum = Number(followReference);
  const followLooksLikeId =
    Number.isFinite(followNum) && followNum > 100000; // heuristic: real message ids are large

  if (followReference && followReference !== "0") {
    if (!followLooksLikeId && includeMessage) {
      return includeMessage;
    }
    return followReference;
  }

  const currentId = selectedMessage?.id ?? detailMessage?.id;
  return currentId ? String(currentId) : null;
};
